import { ThumbsUp, ThumbsDown } from 'lucide-react';
import { cn } from '@/util/cn';
import { IRatingItem } from '@/api/resort/types';
import { formatDateFromStr, MONTH_YEAR_DATE_FORMAT } from '@/util/formatDate';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';

// Individual review card. Renders inside ReviewsList between
// border-b dividers (no per-card box). When the API has translated
// the review into the user's locale, the translated text is shown
// in place of the original.

interface IProps {
  review: IRatingItem;
  className?: string;
}

export const Review = ({ review, className }: IProps) => {
  const { t } = useOTSTranslation('common');
  const name = `${review.firstName ?? ''} ${review.lastName ?? ''}`.trim();
  const body = review.translated?.review || review.review;
  const pros = review.translated?.pros ?? review.pros;
  const cons = review.translated?.cons ?? review.cons;
  const reviewDate = formatDateFromStr(review.date, MONTH_YEAR_DATE_FORMAT);

  return (
    <article className={cn('border-b border-border py-6 last:border-b-0', className)}>
      {(name || reviewDate) && (
        <div className="mb-2 flex flex-wrap items-baseline gap-x-2">
          {name && (
            <h3 className="font-heading text-base font-semibold text-foreground">{name}</h3>
          )}
          {reviewDate && (
            <span className="text-xs text-muted-foreground">{reviewDate}</span>
          )}
        </div>
      )}
      {body && (
        <p className="mb-3 text-sm leading-relaxed text-foreground/90">{body}</p>
      )}
      {(pros || cons) && (
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {pros && (
            <li className="flex items-start gap-2 text-sm">
              <ThumbsUp
                size={16}
                className="mt-0.5 shrink-0 fill-emerald-500 stroke-0 text-emerald-500"
                aria-label={t('resort.reviews.pros')}
              />
              <span className="text-foreground/90">{pros}</span>
            </li>
          )}
          {cons && (
            <li className="flex items-start gap-2 text-sm">
              <ThumbsDown
                size={16}
                className="mt-0.5 shrink-0 fill-red-500 stroke-0 text-red-500"
                aria-label={t('resort.reviews.cons')}
              />
              <span className="text-foreground/90">{cons}</span>
            </li>
          )}
        </ul>
      )}
    </article>
  );
};
